import { layoutBounds, layoutDistance, layoutInBox } from '../dimensions';
import {
  bounds,
  distanceTo,
  shapeInBox,
  unionBounds,
  type Bounds,
  type BoxPlace,
  type Vec2,
} from '../geom';
import type { NodeId } from '../model';
import { layoutsOf, type Drawn, type ResolvedEntity, type ResolvedLayout } from './resolve';

/** The nearest entity, dimension or view under `point`, within the tolerance. */
export function pickNode(drawn: Drawn, point: Vec2, toleranceWorld: number): NodeId | null {
  let best: NodeId | null = null;
  let bestDistance = toleranceWorld;

  for (const entity of drawn.entities) {
    if (!entity.visible || !entity.selectable) continue;
    const distance = distanceTo(entity.shape, point);
    if (distance <= bestDistance) {
      best = entity.id;
      bestDistance = distance;
    }
  }
  for (const annotation of layoutsOf(drawn)) {
    if (!annotation.visible || !annotation.selectable) continue;
    const distance = layoutDistance(annotation.layout, point);
    if (distance <= bestDistance) {
      best = annotation.id;
      bestDistance = distance;
    }
  }

  return best;
}

/** Each visible thing drawn, with its bounds. */
function* boxes(drawn: Drawn): Generator<[NodeId, Bounds]> {
  for (const entity of drawn.entities) {
    if (entity.visible) yield [entity.id, bounds(entity.shape)];
  }
  for (const annotation of layoutsOf(drawn)) {
    if (annotation.visible) yield [annotation.id, layoutBounds(annotation.layout)];
  }
}

/**
 * Where one thing drawn stands against a box, by what it draws rather than its bounds, so a
 * circle around the box or a view's frame around it is clear of it.
 */
export const placeInBox = (item: ResolvedEntity | ResolvedLayout, box: Bounds): BoxPlace =>
  'shape' in item ? shapeInBox(item.shape, box) : layoutInBox(item.layout, box);

/** Window selection takes what is wholly inside; crossing selection also what touches it. */
export function pickInBox(drawn: Drawn, box: Bounds, crossing: boolean): NodeId[] {
  const hits: NodeId[] = [];
  for (const item of [...drawn.entities, ...layoutsOf(drawn)]) {
    if (!item.visible || !item.selectable) continue;
    const place = placeInBox(item, box);
    if (place === 'inside' || (crossing && place !== null)) hits.push(item.id);
  }
  return hits;
}

export function sceneBounds(drawn: Drawn): Bounds | null {
  let all: Bounds | null = null;
  for (const [, around] of boxes(drawn)) all = all ? unionBounds(all, around) : around;
  return all;
}
